import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataType } from "oschema";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  GRID_PAGE_SIZES,
  URL_DEFAULT_LIMIT,
  listWindowToParams,
  readListWindow,
} from "./list-window";
import { useLocation, useSearchParams } from "react-router-dom";

import { previewCacheToken, previewIdFromLocation } from "./preview-as";

import { BulkEditModal } from "../components/BulkEditModal";
import { ConfirmDeleteModal } from "../components/ConfirmDeleteModal";
import { DataTableToolbar } from "../components/DataTableToolbar";
import {
  DynamicDataTable,
  TableDensity,
} from "../components/DynamicDataTable";
import {
  EXPORT_PAGE_SIZE,
  EXPORT_ROW_CAP,
  csvLinesFromRows,
  downloadCsv,
  visibleExportFields,
} from "./csv-export";
import { parseCsvImport } from "./csv-import";
import {
  ACCESS_UPDATING_MESSAGE,
  accessErrorMessage,
  listRuleBroken,
} from "./member-access";
import { auditWriteHeaders, newBatchId } from "./audit-history";
import { deleteRecord } from "./delete-record";
import { latestSuccessAttempt } from "./after-auth";
import { getApi } from "./get-api";
import { mapLimitSettled } from "./map-limit";
import { describeWriteError } from "./pg-write-error";
import { sheetsReconnectFromError } from "./sheets-reconnect";
import { Field, fieldLabel, isReadOnlyField } from "./schema-field";
import { rowKey, rowKeyOrHash } from "./row-key";
import type { DrawerTarget } from "./drawer-url";
import { applyParamPatch, readLiveSearchParams } from "./url-params";
import { buildPayload } from "./validate-record";
import { useAuth } from "./useAuth";
import { useDatasources } from "./useDatasources";
import { changesByRow } from "./sync-rows";
import {
  isSyncBusy,
  useSyncActions,
  useSyncChanges,
  useSyncStatus,
} from "./useSyncState";

interface UseDynamicDataTableProps {
  datasourceId: string;
  collection: string;
  schema?: Field[];
  idField?: string;
  debugMode?: boolean;
  onRowClick?: (row: any) => void;
  onRowDoubleClick?: (row: any) => void;
  onDrawerTarget?: (target: DrawerTarget) => void;
  paginationModel?: GridPaginationModel;
  filterGroups?: string;
  /** Demo datasource: full write UI, but every write is simulated. */
  demoMode?: boolean;
  /** Non-demo read-only datasource: hide all write UI. */
  readOnly?: boolean;
  onClearFilters?: () => void;
  /** Adds one condition to the filter group, for the grid's right-click menu. */
  onAddFilter?: (
    field: string,
    operator: string,
    value: string | boolean
  ) => void;
  filtersNode?: ReactNode;
  hasActiveFilters?: boolean;
  /** Overview card preview: no collection toolbar, no pagination footer. */
  hideToolbar?: boolean;
  hidePagination?: boolean;
}

const DENSITIES: TableDensity[] = ["compact", "standard", "comfortable"];
const DEFAULT_PAGE_SIZE = URL_DEFAULT_LIMIT;
const NO_ROW_IDENTITY =
  "This table has no row identity; edits are not possible";

/** Matching-wide bulk writes page through listData; never reuse EXPORT_ROW_CAP. */
const BULK_SELECT_CAP = 500;
/** Cap in-flight PATCHes/DELETEs so a 500-row match does not open 500 sockets. */
const BULK_WRITE_CONCURRENCY = 4;

function paginationFromParams(
  params: URLSearchParams,
  fallback: GridPaginationModel
): GridPaginationModel {
  if (!params.get("page") && !params.get("limit")) return fallback;
  const window = readListWindow(params, GRID_PAGE_SIZES, fallback.pageSize);
  return { pageSize: window.pageSize, page: window.page - 1 };
}

function paginationToParams(
  model: GridPaginationModel
): Record<string, string | undefined> {
  return listWindowToParams(
    { page: model.page + 1, pageSize: model.pageSize },
    DEFAULT_PAGE_SIZE
  );
}

/** Shown in place of the real warning when the datasource is the demo. */
const DEMO_DELETE_NOTE = (
  <>
    This is the demo dashboard, so nothing is deleted. On your own database this
    runs a <code className="text-od-muted">DELETE</code> statement.
  </>
);

export const useDynamicDataTable = ({
  datasourceId,
  collection,
  schema,
  idField,
  debugMode = false,
  onRowClick,
  onRowDoubleClick,
  onDrawerTarget,
  paginationModel: initialPaginationModel,
  filterGroups,
  demoMode,
  readOnly,
  onClearFilters,
  onAddFilter,
  filtersNode,
  hasActiveFilters,
  hideToolbar = false,
  hidePagination = false,
}: UseDynamicDataTableProps) => {
  // Collection ORs demo into readOnly so + New record stays off. Bulk / import
  // / row-delete chrome still shows; interceptors toast and skip the network.
  const hideWriteChrome = !!readOnly && !demoMode;
  const queryClient = useQueryClient();
  // Every write endpoint on the data controller requires identity. Building a
  // bare Api() here meant row deletes and inline edits went out unauthenticated
  // and came back 401.
  const { token } = useAuth();
  const api = useMemo(() => getApi({ token }), [token]);

  const location = useLocation();
  const previewToken = previewCacheToken(previewIdFromLocation(location));
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    () =>
      paginationFromParams(
        searchParams,
        initialPaginationModel || { pageSize: DEFAULT_PAGE_SIZE, page: 0 }
      )
  );

  const [rowToDelete, setRowToDelete] = useState<any | null>(null);
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [selectMatchingPending, setSelectMatchingPending] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const datasources = useDatasources((s) => s.datasources);
  const datasource = datasources.find((ds) => ds.id === datasourceId);
  // The status routes answer for Google Sheets datasources only (ARD 9.6).
  const sheetsSync =
    !!datasource && String(datasource.type) === "GOOGLE_SHEETS" && !demoMode;
  /**
   * ARD 9.2 and 9.6: a Sheets datasource stores `spreadsheetId`. The sync state
   * API (MOSA-432) adds it to the datasource answer. The generated DTO is older
   * than that API, so read it through a cast rather than edit generated code.
   * Without the id the row marker still shows, but "Open in Google Sheets" has
   * no link to open.
   */
  const spreadsheetId =
    (datasource as { spreadsheetId?: unknown } | undefined)?.spreadsheetId;
  const syncSpreadsheetId =
    typeof spreadsheetId === "string" && spreadsheetId ? spreadsheetId : null;
  // The status query is the source of the poll cadence (ARD 9.6). The changes
  // query cannot tell that a change is queued until it lands in the list.
  const syncStatusQuery = useSyncStatus(datasourceId, sheetsSync);
  const syncChangesQuery = useSyncChanges(
    datasourceId,
    sheetsSync,
    isSyncBusy(syncStatusQuery.data)
  );
  const syncActions = useSyncActions(datasourceId);
  const collectionDto = latestSuccessAttempt(
    datasources.find((ds) => ds.id === datasourceId)?.analyzeAttempts
  )?.databaseCollections?.find((c) => c.name === collection);

  const setSearchParamsRef = useRef(setSearchParams);
  const selectMatchingGen = useRef(0);
  const bulkBusyRef = useRef(false);
  const filterGroupsRef = useRef(filterGroups);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  const patchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const next = applyParamPatch(readLiveSearchParams(), updates);
      setSearchParamsRef.current(next, { replace: true });
    },
    []
  );

  /* ------------------------------------------------- preferences from URL */

  const [expandedRelations, setExpandedRelations] = useState<string[]>(() =>
    (searchParams.get("expand") || "").split(",").filter(Boolean)
  );

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    const hidden = (searchParams.get("hiddenColumns") || "")
      .split(",")
      .filter(Boolean);
    return Object.fromEntries(hidden.map((col) => [col, false]));
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    (searchParams.get("columnOrder") || "").split(",").filter(Boolean)
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    const raw = searchParams.get("sort");
    if (!raw) return [];
    const [field, direction] = raw.split(":");
    return field ? [{ field, sort: direction === "desc" ? "desc" : "asc" }] : [];
  });

  const [search, setSearch] = useState<string>(
    () => searchParams.get("q") || ""
  );

  const [density, setDensity] = useState<TableDensity>(() => {
    const raw = searchParams.get("density") as TableDensity;
    return DENSITIES.includes(raw) ? raw : "comfortable";
  });

  // Re-read every preference when the URL changes so a shared link restores the
  // exact view the sender was looking at.
  useEffect(() => {
    const nextExpanded = (searchParams.get("expand") || "")
      .split(",")
      .filter(Boolean);
    setExpandedRelations((prev) =>
      prev.join(",") === nextExpanded.join(",") ? prev : nextExpanded
    );

    const hidden = (searchParams.get("hiddenColumns") || "")
      .split(",")
      .filter(Boolean);
    const nextVisibility = Object.fromEntries(hidden.map((c) => [c, false]));
    setColumnVisibility((prev) => {
      Object.entries(prev).forEach(([column, visible]) => {
        if (visible && !(column in nextVisibility)) {
          nextVisibility[column] = true;
        }
      });
      return JSON.stringify(prev) === JSON.stringify(nextVisibility)
        ? prev
        : nextVisibility;
    });

    const nextOrder = (searchParams.get("columnOrder") || "")
      .split(",")
      .filter(Boolean);
    setColumnOrder((prev) =>
      prev.join(",") === nextOrder.join(",") ? prev : nextOrder
    );

    setSearch((prev) => {
      const next = searchParams.get("q") || "";
      return prev === next ? prev : next;
    });

    const nextPage = paginationFromParams(searchParams, {
      pageSize: DEFAULT_PAGE_SIZE,
      page: 0,
    });
    setPaginationModel((prev) =>
      prev.page === nextPage.page && prev.pageSize === nextPage.pageSize
        ? prev
        : nextPage
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleExpansionChange = useCallback(
    (relations: string[]) => {
      setExpandedRelations(relations);
      patchParams({ expand: relations.length ? relations.join(",") : undefined });
    },
    [patchParams]
  );

  const handleColumnVisibilityChange = useCallback(
    (newModel: { [key: string]: boolean }) => {
      setColumnVisibility(newModel);
      const hidden = Object.keys(newModel).filter((key) => !newModel[key]);
      patchParams({ hiddenColumns: hidden.length ? hidden.join(",") : undefined });
    },
    [patchParams]
  );

  const handleColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      setColumnOrder(newOrder);
      patchParams({ columnOrder: newOrder.length ? newOrder.join(",") : undefined });
    },
    [patchParams]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      const next = { ...paginationModel, page: 0 };
      setPaginationModel(next);
      const first = model[0];
      patchParams({
        sort: first ? `${first.field}:${first.sort ?? "asc"}` : undefined,
        ...paginationToParams(next),
      });
    },
    [paginationModel, patchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      const next = { ...paginationModel, page: 0 };
      setPaginationModel(next);
      patchParams({ q: value || undefined, ...paginationToParams(next) });
    },
    [paginationModel, patchParams]
  );

  const handleDensityChange = useCallback(
    (value: TableDensity) => {
      setDensity(value);
      patchParams({ density: value === "comfortable" ? undefined : value });
    },
    [patchParams]
  );

  /* ------------------------------------------------------------- querying */

  /**
   * Every scalar column is searchable: the API casts to text before matching,
   * so a search finds a budget of 1234 as readily as a name. Booleans and JSON
   * blobs are left out because matching "true" or a brace in them is noise.
   */
  const searchableFields = useMemo(
    () =>
      (schema ?? []).filter(
        (field) =>
          !field.isArray &&
          field.type !== DataType.BOOLEAN &&
          field.type !== DataType.OBJECT &&
          field.subType !== "JSON"
      ),
    [schema]
  );

  /**
   * The free-text box becomes an OR group of `contains` filters, AND-ed with
   * whatever the filter chips already asked for.
   */
  const effectiveFilterGroups = useMemo(() => {
    const term = search.trim();
    const searchGroup =
      term && searchableFields.length
        ? {
            operator: "OR" as const,
            filters: searchableFields.map((field) => ({
              field: field.name,
              operator: "contains",
              value: term,
            })),
          }
        : null;

    if (!searchGroup) return filterGroups;

    let base: any = null;
    if (filterGroups) {
      try {
        base = JSON.parse(filterGroups);
      } catch {
        base = null;
      }
    }
    if (!base) return JSON.stringify({ operator: "AND", groups: [searchGroup] });

    return JSON.stringify({
      operator: "AND",
      groups: [base, searchGroup],
    });
  }, [search, searchableFields, filterGroups]);

  /**
   * The key column, preferred from the schema so a table whose key is not
   * called "id" still sorts and pages deterministically.
   */
  const keyField = useMemo(() => {
    const primary = (schema ?? []).find((f) => f.primary);
    if (primary) return primary.name;
    if (idField && (schema ?? []).some((f) => f.name === idField)) return idField;
    return (schema ?? []).find((f) => /^(id|_id|uuid)$/i.test(f.name))?.name;
  }, [schema, idField]);

  /**
   * Without an ORDER BY, Postgres returns rows in physical order, so editing a
   * row moves it to the end of the table and LIMIT/OFFSET paging can show the
   * same row twice or skip one. Falling back to the key column keeps the view
   * stable.
   */
  const sort = useMemo(() => {
    const first = sortModel[0];
    if (first) return `${first.field}:${first.sort ?? "asc"}`;
    return keyField ? `${keyField}:asc` : undefined;
  }, [sortModel, keyField]);

  const queryKey = useMemo(
    () => [
      "tableData",
      datasourceId,
      collection,
      {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        expand: expandedRelations.join(",") || undefined,
        filterGroups: effectiveFilterGroups,
        sort,
        preview: previewToken,
      },
    ],
    [
      datasourceId,
      collection,
      paginationModel.page,
      paginationModel.pageSize,
      expandedRelations,
      effectiveFilterGroups,
      sort,
      previewToken,
    ]
  );

  const {
    data: queryData,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.data.listData(datasourceId, collection, {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        expand: expandedRelations.join(",") || undefined,
        filterGroups: effectiveFilterGroups,
        sort,
      });
      return response.data;
    },
    enabled: !!datasourceId && !!collection,
    staleTime: 30000,
    // A page change can keep the previous page on screen. A preview change
    // must not: that previous page is the other person's rows.
    placeholderData: (previous, previousQuery) => {
      const prev = previousQuery?.queryKey?.[3] as
        | { preview?: string }
        | undefined;
      if (!prev || prev.preview !== previewToken) return undefined;
      return previous;
    },
    select: (response) => ({
      items: response.items || [],
      total: response.total || 0,
      ruleBroken: listRuleBroken(response as { rule_broken?: unknown }),
    }),
  });

  const ruleBroken = queryData?.ruleBroken === true;
  // A broken rule is zero rows. The notice replaces the empty-sheet copy.
  const data = useMemo(
    () => (ruleBroken ? [] : queryData?.items || []),
    [queryData?.items, ruleBroken],
  );
  const total = ruleBroken ? 0 : queryData?.total || 0;

  const syncPending = syncChangesQuery.isPending;
  const syncReady = sheetsSync && !syncPending;

  /** Row id -> newest queued change. Only Sheets datasources ask for it. */
  const syncByRow = useMemo(
    () =>
      sheetsSync && syncChangesQuery.data
        ? changesByRow(syncChangesQuery.data, data, idField)
        : undefined,
    [sheetsSync, syncChangesQuery.data, data, idField]
  );
  const reconnect = queryError ? sheetsReconnectFromError(queryError) : null;
  const error =
    queryError && !reconnect
      ? accessErrorMessage(queryError, "Could not load these records")
      : null;

  // A new matching set is a new view: drop back to page 1 so filter chips
  // cannot leave you on an empty page of a previous result.
  useEffect(() => {
    if (filterGroupsRef.current === filterGroups) return;
    filterGroupsRef.current = filterGroups;
    if (paginationModel.page === 0) return;
    const next = { ...paginationModel, page: 0 };
    setPaginationModel(next);
    patchParams(paginationToParams(next));
  }, [filterGroups, paginationModel, patchParams]);

  // After a filter/search shrinks the matching set, the current page can sit
  // past the last page. Pull it back so the grid is not empty.
  useEffect(() => {
    if (isLoading) return;
    const lastPage = Math.max(
      0,
      Math.ceil((total || 0) / paginationModel.pageSize) - 1
    );
    if (paginationModel.page <= lastPage) return;
    const next = { ...paginationModel, page: lastPage };
    setPaginationModel(next);
    patchParams(paginationToParams(next));
  }, [isLoading, total, paginationModel, patchParams]);

  // Matching-wide ids belong to the current filter/search, not the page.
  // Paging must not clear them; a new matching set must.
  useEffect(() => {
    selectMatchingGen.current += 1;
    setSelectMatchingPending(false);
    setSelectedIds((prev) => (prev.length ? [] : prev));
  }, [effectiveFilterGroups, search, datasourceId, collection]);

  /* ------------------------------------------------- relation bookkeeping */

  const expandableFieldsMap = useMemo<
    Map<
      string,
      { relationName: string; path: string; level: number; parentPath?: string }
    >
  >(() => {
    const expandable = new Map<
      string,
      { relationName: string; path: string; level: number; parentPath?: string }
    >();

    (schema ?? []).forEach((field) => {
      const match = field.name.match(/^(.+?)(Id|_id)$/);
      if (match) {
        expandable.set(field.name, {
          relationName: match[1],
          path: match[1],
          level: 0,
        });
      }
    });

    const sampleRow = data[0] as Record<string, any> | undefined;
    if (sampleRow) {
      Object.keys(sampleRow).forEach((key) => {
        const value = sampleRow[key];
        if (value && typeof value === "object" && !Array.isArray(value)) {
          Object.keys(value).forEach((nested) => {
            const match = nested.match(/^(.+?)(Id|_id)$/);
            if (match) {
              expandable.set(`${key}.${nested}`, {
                relationName: match[1],
                path: `${key}.${match[1]}`,
                level: key.split(".").length,
                parentPath: key,
              });
            }
          });
        }
      });
    }

    return expandable;
  }, [schema, data]);

  const toolbarColumns: GridColDef[] = useMemo(() => {
    if (!schema) return [];

    const cols: GridColDef[] = (schema ?? []).map(
      (field) =>
        ({ field: field.name, headerName: fieldLabel(field), width: 150 } as GridColDef)
    );

    const sampleRow = data[0] as Record<string, any> | undefined;
    if (sampleRow) {
      expandedRelations.forEach((relationPath) => {
        const related = sampleRow[relationPath];
        if (!related || typeof related !== "object") return;
        const priority = ["name", "title", "label", "status", "email", "description"];
        const available = Object.keys(related);
        const ordered = [...priority, ...available.filter((f) => !priority.includes(f))];
        ordered
          .filter((f) => available.includes(f))
          .forEach((subField) => {
            cols.push({
              field: `__expanded_${relationPath}_${subField}`,
              headerName: `${relationPath} › ${subField}`,
              width: 180,
            } as GridColDef);
          });
      });
    }

    return cols;
  }, [schema, expandedRelations, data]);

  const handleToggleExpansion = useCallback(
    (path: string) => {
      const next = new Set(expandedRelations);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      handleExpansionChange(Array.from(next));
    },
    [expandedRelations, handleExpansionChange]
  );

  const handleExpandAll = useCallback(() => {
    handleExpansionChange(
      Array.from(new Set(Array.from(expandableFieldsMap.values()).map((i) => i.path)))
    );
  }, [expandableFieldsMap, handleExpansionChange]);

  const handleCollapseAll = useCallback(
    () => handleExpansionChange([]),
    [handleExpansionChange]
  );

  const onPaginationChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      patchParams(paginationToParams(model));
    },
    [patchParams]
  );

  /* ------------------------------------------------------------ mutations */

  const updateMutation = useMutation({
    mutationFn: async ({
      row,
      original,
    }: {
      row: any;
      original?: any;
    }) => {
      // Same payload rule the form uses: only fields that actually changed, no
      // primary keys, no audit timestamps. Sending the whole row here was what
      // let one edit overwrite columns the user never touched.
      const id = rowKey(original ?? row, idField);
      if (id == null) {
        throw new Error(NO_ROW_IDENTITY);
      }
      const payload = buildPayload(schema ?? [], row, original);
      // ARD 9: a Sheets row carries the hash it was read with. The queue
      // compares it with the sheet before it writes, so an edit that does not
      // echo it is refused with `row_hash_required`.
      const rowHash = (original ?? row)?._rowHash;
      if (sheetsSync && typeof rowHash === "string") {
        payload._rowHash = rowHash;
      }
      return api.data.updateData(datasourceId, collection, id, payload);
    },
    /**
     * ARD 9.6 "optimistic edit": the cell shows the new value at once, with
     * `Pending`. The grid model holds the typed value, but the table query
     * would bring the old Google value back on the next read, so write the
     * optimistic row into the cache too. A later list answer replaces it.
     */
    onMutate: async ({ row, original }) => {
      if (!sheetsSync) return { previousData: undefined };
      await queryClient.cancelQueries({ queryKey: ["tableData", datasourceId] });
      const snapshot = queryClient.getQueriesData({
        queryKey: ["tableData", datasourceId],
      });
      const id = rowKey(original ?? row, idField);
      queryClient.setQueriesData(
        { queryKey: ["tableData", datasourceId] },
        (old: any) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((item: any) =>
              rowKey(item, idField) === id
                ? {
                    ...item,
                    ...payloadWithoutKeys(row, original, schema),
                    _sync: item?._sync ?? { changeId: "", status: "PENDING" },
                  }
                : item
            ),
          };
        }
      );
      return { previousData: snapshot };
    },
    onSuccess: (response, vars) => {
      // ARD 9.4 / SheetsQueuedResponse: only a 202 is queued. The grid keeps
      // the typed value with "Pending" until the sync status moves. A 200 is a
      // direct write (flag off), so it is in the table at once and must
      // refetch. Branch on HTTP status, not on the body shape.
      const axiosResponse = response as
        | { status?: number; data?: { items?: any[]; changes?: any[] } }
        | undefined;
      const status = axiosResponse?.status;
      const queued = sheetsSync && status === 202;
      toast.success(queued ? "Change queued" : "Record updated");
      if (queued) {
        // Prefer the optimistic `items` from the 202 body so `_sync.changeId`
        // matches the queued change before the next poll.
        const queuedItems = axiosResponse?.data?.items;
        if (Array.isArray(queuedItems) && queuedItems.length > 0) {
          const id = rowKey(vars.original ?? vars.row, idField);
          queryClient.setQueriesData(
            { queryKey: ["tableData", datasourceId] },
            (old: any) => {
              if (!old?.items) return old;
              const byId = new Map(
                queuedItems.map((item: any) => [rowKey(item, idField), item])
              );
              return {
                ...old,
                items: old.items.map((item: any) => {
                  const key = rowKey(item, idField);
                  const next = key != null ? byId.get(key) : undefined;
                  if (next) return { ...item, ...next };
                  if (key === id && queuedItems.length === 1) {
                    return { ...item, ...queuedItems[0] };
                  }
                  return item;
                }),
              };
            }
          );
        }
        queryClient.invalidateQueries({
          queryKey: ["syncChanges", datasourceId],
        });
        queryClient.invalidateQueries({
          queryKey: ["syncStatus", datasourceId],
        });
      } else {
        // A direct write is already in the table: refetch the real value and
        // drop the optimistic row.
        queryClient.invalidateQueries({
          queryKey: ["tableData", datasourceId, collection],
        });
      }
    },
    onError: (err: any, _vars, context) => {
      toast.error(
        describeWriteError(err, schema).message || "Could not save that change"
      );
      if (context?.previousData) {
        context.previousData.forEach(([key, data]: [any, any]) =>
          queryClient.setQueryData(key, data)
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["tableData", datasourceId, collection],
      });
    },
  });

  const handleRowUpdate = useCallback(
    (row: any, original?: any) => {
      const id = rowKey(original ?? row, idField);
      if (id == null) {
        toast.error(NO_ROW_IDENTITY);
        throw new Error(NO_ROW_IDENTITY);
      }
      updateMutation.mutate({ row, original });
    },
    [updateMutation, idField]
  );

  const deleteMutation = useMutation({
    mutationFn: async ({ id, rowHash }: { id: string; rowHash?: unknown }) => {
      return deleteRecord(api, datasourceId, collection, id, { rowHash });
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: (old.items ?? []).filter(
            (item: any) => rowKey(item, idField) !== String(id)
          ),
          total: Math.max(0, (old.total ?? 1) - 1),
        };
      });
      return { previousData };
    },
    onSuccess: () => toast.success("Record deleted"),
    onError: (err: any, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error(
        describeWriteError(err, schema).message || "Could not delete that record"
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["tableData", datasourceId, collection],
      });
    },
  });

  const confirmDelete = useCallback(() => {
    if (!rowToDelete) return;
    if (demoMode) {
      toast.success("Demo only. The demo database is not modified.");
      setRowToDelete(null);
      return;
    }
    const id = rowKey(rowToDelete, idField);
    if (id == null) {
      toast.error(NO_ROW_IDENTITY);
      setRowToDelete(null);
      return;
    }
    deleteMutation.mutate({ id, rowHash: rowToDelete?._rowHash });
    setRowToDelete(null);
  }, [rowToDelete, deleteMutation, idField, demoMode]);

  /**
   * There is no batch endpoint, so this is N deletes. allSettled rather than
   * all: one row failing a foreign-key constraint should not hide the fact that
   * the other nine were removed, and the caller needs the real count either way.
   */
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: GridRowSelectionModel) => {
      const list = [...ids];
      setBulkProgress({ done: 0, total: list.length });
      // One batch id for the whole action, so the owner's Activity list reads
      // "Deleted 9 rows" rather than nine separate deletes (MOSA-368 slice 1).
      const headers = auditWriteHeaders({
        batchId: newBatchId(),
        action: "bulk",
      });
      const results = await mapLimitSettled(
        list,
        BULK_WRITE_CONCURRENCY,
        (id) =>
          deleteRecord(api, datasourceId, collection, String(id), {
            rowHash: data.find(
              (row: any) => rowKey(row, idField) === String(id)
            )?._rowHash,
            headers,
          }),
        (done, total) => setBulkProgress({ done, total })
      );
      const rejected = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      return {
        deleted: results.length - rejected.length,
        failed: rejected.length,
        firstError: rejected[0]?.reason,
      };
    },
    onSuccess: ({ deleted, failed, firstError }) => {
      if (deleted) {
        toast.success(`Deleted ${deleted} ${deleted === 1 ? "record" : "records"}`);
      }
      if (failed) {
        const detail = describeWriteError(firstError, schema).message;
        toast.error(
          `${failed} ${failed === 1 ? "record" : "records"} could not be deleted. ${detail}`
        );
      }
      setSelectedIds([]);
    },
    onError: (err) =>
      toast.error(
        describeWriteError(err, schema).message || "Could not delete those records"
      ),
    onSettled: () => {
      bulkBusyRef.current = false;
      setBulkDeleteOpen(false);
      setBulkProgress(null);
      queryClient.invalidateQueries({
        queryKey: ["tableData", datasourceId, collection],
      });
    },
  });

  const resolveSelectedWriteIds = useCallback(
    (ids: GridRowSelectionModel): string[] | undefined => {
      const resolved: string[] = [];
      for (const selected of ids) {
        const row = data.find(
          (item: any) => rowKeyOrHash(item, idField) === String(selected)
        );
        if (row) {
          const key = rowKey(row, idField);
          if (key == null) {
            toast.error(NO_ROW_IDENTITY);
            return undefined;
          }
          resolved.push(key);
        } else {
          // Off-page ids come from matchingIdOf (already a rowKey). A hash
          // means the grid had no identity when the row was selected.
          if (String(selected).startsWith("row_")) {
            toast.error(NO_ROW_IDENTITY);
            return undefined;
          }
          resolved.push(String(selected));
        }
      }
      return resolved;
    },
    [data, idField]
  );

  const confirmBulkDelete = useCallback(() => {
    if (bulkBusyRef.current || bulkDeleteMutation.isPending) return;
    if (demoMode) {
      toast.success("Demo only. The demo database is not modified.");
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      return;
    }
    const ids = resolveSelectedWriteIds(selectedIds);
    if (!ids) return;
    bulkBusyRef.current = true;
    setBulkProgress({ done: 0, total: ids.length });
    bulkDeleteMutation.mutate(ids);
  }, [
    bulkDeleteMutation,
    selectedIds,
    demoMode,
    resolveSelectedWriteIds,
  ]);

  /**
   * There is no batch endpoint, so this is N PATCHes. allSettled rather than
   * all: one row failing a constraint should not hide the fact that the other
   * nine were updated, and the caller needs the real count either way.
   */
  const bulkEditMutation = useMutation({
    mutationFn: async ({
      ids,
      payload,
    }: {
      ids: GridRowSelectionModel;
      payload: Record<string, any>;
    }) => {
      const list = [...ids];
      setBulkProgress({ done: 0, total: list.length });
      // One Activity entry that opens to the rows it changed, rather than one
      // entry per row (MOSA-368 slice 1).
      const headers = auditWriteHeaders({
        batchId: newBatchId(),
        action: "bulk",
      });
      const results = await mapLimitSettled(
        list,
        BULK_WRITE_CONCURRENCY,
        (id) => {
          const row = data.find(
            (candidate: any) => rowKey(candidate, idField) === String(id)
          );
          // ARD 9: echo the hash the row was read with, the same way a single
          // edit does. It is also where the history reads the old values.
          const rowHash = row?._rowHash;
          const body =
            sheetsSync && typeof rowHash === "string"
              ? { ...payload, _rowHash: rowHash }
              : payload;
          return api.data.updateData(
            datasourceId,
            collection,
            String(id),
            body,
            { headers }
          );
        },
        (done, total) => setBulkProgress({ done, total })
      );
      const rejected = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      return {
        updated: results.length - rejected.length,
        failed: rejected.length,
        firstError: rejected[0]?.reason,
      };
    },
    onSuccess: ({ updated, failed, firstError }) => {
      if (updated) {
        toast.success(
          `Updated ${updated} ${updated === 1 ? "record" : "records"}`
        );
        setSelectedIds([]);
      }
      if (failed) {
        const detail = describeWriteError(firstError, schema).message;
        toast.error(
          `${failed} ${failed === 1 ? "record" : "records"} could not be updated. ${detail}`
        );
      }
    },
    onError: (err) =>
      toast.error(
        describeWriteError(err, schema).message || "Could not update those records"
      ),
    onSettled: () => {
      bulkBusyRef.current = false;
      setBulkEditOpen(false);
      setBulkProgress(null);
      queryClient.invalidateQueries({
        queryKey: ["tableData", datasourceId, collection],
      });
    },
  });

  const applyBulkEdit = useCallback(
    (values: Record<string, any>) => {
      if (bulkBusyRef.current || bulkEditMutation.isPending) return;
      const writable = (schema ?? []).filter((field) => !isReadOnlyField(field));
      const chosenFields = writable.filter((field) =>
        Object.prototype.hasOwnProperty.call(values, field.name)
      );
      if (chosenFields.length === 0) return;

      if (demoMode) {
        toast.success("Demo only. The demo database is not modified.");
        setSelectedIds([]);
        setBulkEditOpen(false);
        return;
      }

      // Omit original so every chosen key is included, including clears.
      const payload = buildPayload(schema ?? [], Object.fromEntries(
        chosenFields.map((field) => [field.name, values[field.name] ?? ""])
      ));
      if (Object.keys(payload).length === 0) return;

      const ids = resolveSelectedWriteIds(selectedIds);
      if (!ids) return;

      bulkBusyRef.current = true;
      setBulkProgress({ done: 0, total: ids.length });
      bulkEditMutation.mutate({ ids, payload });
    },
    [schema, demoMode, bulkEditMutation, selectedIds, resolveSelectedWriteIds]
  );

  /**
   * There is no batch create endpoint, so this is N POSTs. allSettled rather
   * than all: one row failing a constraint should not hide the fact that the
   * other nine were created.
   */
  const importMutation = useMutation({
    mutationFn: async ({
      rows,
      truncated,
    }: {
      rows: Record<string, string>[];
      truncated: boolean;
    }) => {
      const list = rows;
      setBulkProgress({ done: 0, total: list.length });
      // An import is one Activity entry that opens to the rows it added
      // (MOSA-368 slice 1, A8).
      const headers = auditWriteHeaders({
        batchId: newBatchId(),
        action: "import",
      });
      const results = await mapLimitSettled(
        list,
        BULK_WRITE_CONCURRENCY,
        (row) =>
          api.data.createData(
            datasourceId,
            collection,
            buildPayload(schema ?? [], row),
            { headers }
          ),
        (done, total) => setBulkProgress({ done, total })
      );
      const rejected = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      return {
        created: results.length - rejected.length,
        failed: rejected.length,
        firstError: rejected[0]?.reason,
        truncated,
      };
    },
    onSuccess: ({ created, failed, firstError, truncated }) => {
      if (created) {
        toast.success(
          truncated
            ? `Imported ${created} rows (capped at ${EXPORT_ROW_CAP})`
            : `Imported ${created} ${created === 1 ? "row" : "rows"}`
        );
      }
      if (failed) {
        const detail = describeWriteError(firstError, schema).message;
        toast.error(
          `${failed} ${failed === 1 ? "row" : "rows"} could not be imported. ${detail}`
        );
      }
    },
    onError: (err) =>
      toast.error(
        describeWriteError(err, schema).message || "Could not import those records"
      ),
    onSettled: () => {
      bulkBusyRef.current = false;
      setBulkProgress(null);
      setImportOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["tableData", datasourceId, collection],
      });
    },
  });

  /* ------------------------------------------ matching-wide list paging */

  const fetchMatchingRows = useCallback(
    async (cap: number): Promise<any[]> => {
      const rows: any[] = [];
      let page = 1;
      while (rows.length < cap) {
        const response = await api.data.listData(datasourceId, collection, {
          page,
          limit: EXPORT_PAGE_SIZE,
          expand: expandedRelations.join(",") || undefined,
          filterGroups: effectiveFilterGroups,
          sort,
        });
        const items = response.data.items || [];
        if (!items.length) break;
        const room = cap - rows.length;
        rows.push(...items.slice(0, room));
        const reportedTotal = response.data.total ?? total;
        if (rows.length >= cap) break;
        if (items.length < EXPORT_PAGE_SIZE) break;
        if (reportedTotal && rows.length >= reportedTotal) break;
        page += 1;
      }
      return rows;
    },
    [
      api,
      datasourceId,
      collection,
      expandedRelations,
      effectiveFilterGroups,
      sort,
      total,
    ]
  );

  const matchingIdOf = useCallback(
    (row: any): string | null => rowKey(row, idField) ?? null,
    [idField]
  );

  const handleSelectMatching = useCallback(async () => {
    if (selectMatchingPending) return;
    const gen = ++selectMatchingGen.current;
    setSelectMatchingPending(true);
    try {
      const rows = await fetchMatchingRows(BULK_SELECT_CAP);
      if (gen !== selectMatchingGen.current) return;
      const ids: Array<string | number> = [];
      for (const row of rows) {
        if (ids.length >= BULK_SELECT_CAP) break;
        const id = matchingIdOf(row);
        if (id == null) continue;
        ids.push(id);
      }
      if (!ids.length) {
        toast.error("Could not select matching records");
        return;
      }
      setSelectedIds(ids);
      if (total > BULK_SELECT_CAP) {
        toast.success(
          `Selected first ${ids.length} matching records (capped at ${BULK_SELECT_CAP})`
        );
      }
    } catch (err: any) {
      if (gen !== selectMatchingGen.current) return;
      toast.error(
        accessErrorMessage(err, "Could not load matching records")
      );
    } finally {
      if (gen === selectMatchingGen.current) {
        setSelectMatchingPending(false);
      }
    }
  }, [
    selectMatchingPending,
    fetchMatchingRows,
    matchingIdOf,
    total,
  ]);

  /* -------------------------------------------------------------- export */

  const handleExport = useCallback(async () => {
    const fields = visibleExportFields(
      schema ?? [],
      columnVisibility,
      columnOrder
    );
    if (!fields.length) {
      toast.error("No visible columns to export");
      return;
    }

    const exportingSelection = selectedIds.length > 0;
    let rows: any[];
    let capped = false;

    if (exportingSelection) {
      const wanted = new Set(selectedIds.map(String));
      rows = data.filter((row: any) => wanted.has(rowKeyOrHash(row, idField)));
      if (rows.length < selectedIds.length) {
        try {
          const wanted = new Set(selectedIds.map(String));
          const matching = await fetchMatchingRows(BULK_SELECT_CAP);
          rows = matching.filter((row) => {
            const id = matchingIdOf(row);
            return id != null && wanted.has(id);
          });
        } catch (err: any) {
          toast.error(
            accessErrorMessage(err, "Could not export those records")
          );
          return;
        }
      }
      if (!rows.length) {
        toast.error("No selected rows to export");
        return;
      }
    } else {
      if (!data.length) return;
      try {
        rows = await fetchMatchingRows(EXPORT_ROW_CAP);
      } catch (err: any) {
        toast.error(
          accessErrorMessage(err, "Could not export those records")
        );
        return;
      }
      if (!rows.length) {
        toast.error("No rows to export");
        return;
      }
      capped = total > EXPORT_ROW_CAP;
    }

    downloadCsv(
      exportingSelection
        ? `${collection}-selected.csv`
        : `${collection}.csv`,
      csvLinesFromRows(rows, fields)
    );

    if (capped) {
      toast.success(
        `Exported ${rows.length} rows (capped at ${EXPORT_ROW_CAP})`
      );
    } else {
      toast.success(
        `Exported ${rows.length} ${rows.length === 1 ? "row" : "rows"}`
      );
    }
  }, [
    schema,
    columnVisibility,
    columnOrder,
    selectedIds,
    data,
    idField,
    fetchMatchingRows,
    matchingIdOf,
    total,
    collection,
  ]);

  const handleImport = useCallback(
    async (file: File) => {
      if (bulkBusyRef.current || importMutation.isPending) return;
      if (!schema?.length) {
        toast.error("No columns to import against");
        return;
      }

      let text: string;
      try {
        text = await file.text();
      } catch {
        toast.error("Could not read that file");
        return;
      }

      const { rows, truncated } = parseCsvImport(
        text,
        schema,
        EXPORT_ROW_CAP
      );
      if (!rows.length) {
        toast.error("No rows to import");
        return;
      }

      if (demoMode) {
        toast.success("Demo only. The demo database is not modified.");
        return;
      }

      bulkBusyRef.current = true;
      setImportOpen(true);
      setBulkProgress({ done: 0, total: rows.length });
      importMutation.mutate({ rows, truncated });
    },
    [schema, demoMode, importMutation]
  );

  const exportLabel =
    selectedIds.length > 0
      ? `Export ${selectedIds.length} selected ${
          selectedIds.length === 1 ? "record" : "records"
        } as CSV`
      : "Export all matching records as CSV";

  /* --------------------------------------------------------------- render */

  const Toolbar = useMemo(
    () =>
      hideToolbar ? null : (
        <DataTableToolbar
          columns={toolbarColumns}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          expandableFields={expandableFieldsMap}
          expandedPaths={expandedRelations}
          onToggleExpansion={handleToggleExpansion}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          search={search}
          onSearchChange={handleSearchChange}
          searchableCount={searchableFields.length}
          searchPlaceholder={`Search ${collection}…`}
          density={density}
          onDensityChange={handleDensityChange}
          onRefresh={() => refetch()}
          refreshing={isFetching}
          onExport={data.length ? handleExport : undefined}
          exportLabel={exportLabel}
          onImport={hideWriteChrome ? undefined : handleImport}
          importLabel="Import records from CSV"
          filters={filtersNode}
        />
      ),
    [
      hideToolbar,
      toolbarColumns,
      columnVisibility,
      handleColumnVisibilityChange,
      expandableFieldsMap,
      expandedRelations,
      handleToggleExpansion,
      handleExpandAll,
      handleCollapseAll,
      search,
      handleSearchChange,
      searchableFields.length,
      collection,
      density,
      handleDensityChange,
      refetch,
      isFetching,
      data.length,
      handleExport,
      exportLabel,
      handleImport,
      hideWriteChrome,
      filtersNode,
    ]
  );

  /**
   * A memoised element rather than a component. Returning a new component type
   * on every render made React unmount and remount the whole DataGrid, which
   * threw away scroll position, focus and column widths on every keystroke.
   */
  const DataTable = useMemo(
    () => (
      <>
        <DynamicDataTable
          datasourceId={datasourceId}
          collection={collection}
          data={data}
          loading={
            isLoading ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            bulkDeleteMutation.isPending ||
            bulkEditMutation.isPending ||
            importMutation.isPending
          }
          total={total}
          fields={schema}
          idField={idField}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowUpdate={readOnly ? undefined : handleRowUpdate}
          onDelete={hideWriteChrome ? undefined : setRowToDelete}
          onAddFilter={onAddFilter}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          onBulkDelete={
            hideWriteChrome ? undefined : () => setBulkDeleteOpen(true)
          }
          onBulkEdit={hideWriteChrome ? undefined : () => setBulkEditOpen(true)}
          onBulkExport={hideWriteChrome ? undefined : handleExport}
          onSelectMatching={hideWriteChrome ? undefined : handleSelectMatching}
          selectMatchingPending={selectMatchingPending}
          matchingSelectCap={BULK_SELECT_CAP}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          onDrawerTarget={onDrawerTarget}
          debugMode={debugMode}
          paginationModel={paginationModel}
          paginationChange={onPaginationChange}
          onRefresh={refetch}
          expandableFields={expandedRelations}
          onExpansionChange={handleExpansionChange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          expandableFieldsMap={expandableFieldsMap}
          density={density}
          demoMode={demoMode}
          readOnly={readOnly}
          hidePagination={hidePagination}
          syncReady={syncReady}
          syncChanges={syncByRow}
          syncSpreadsheetId={syncSpreadsheetId}
          onSyncRetry={syncActions.retry}
          onSyncDismiss={syncActions.dismiss}
          hasActiveFilters={hasActiveFilters || !!search.trim()}
          emptySuppressed={!!reconnect}
          accessNotice={ruleBroken ? ACCESS_UPDATING_MESSAGE : null}
          onClearFilters={
            search.trim()
              ? () => {
                  handleSearchChange("");
                  onClearFilters?.();
                }
              : onClearFilters
          }
        />
        {!hideWriteChrome && (
        <ConfirmDeleteModal
          open={!!rowToDelete}
          onCancel={() => setRowToDelete(null)}
          onConfirm={confirmDelete}
          description={describeRow(rowToDelete, idField)}
        />
        )}
        <ConfirmDeleteModal
          open={bulkDeleteOpen}
          onCancel={() => {
            if (bulkDeleteMutation.isPending) return;
            setBulkDeleteOpen(false);
          }}
          onConfirm={confirmBulkDelete}
          title={`Delete ${selectedIds.length} ${
            selectedIds.length === 1 ? "record" : "records"
          }?`}
          confirmLabel={`Delete ${selectedIds.length}`}
          applying={
            bulkDeleteMutation.isPending ||
            (bulkDeleteOpen && bulkProgress != null)
          }
          progress={
            bulkProgress &&
            (bulkDeleteMutation.isPending || bulkDeleteOpen)
              ? `Deleting ${bulkProgress.done} of ${bulkProgress.total}`
              : undefined
          }
          body={
            demoMode ? (
              DEMO_DELETE_NOTE
            ) : (
              <>
                This writes {selectedIds.length} separate{" "}
                <code className="text-od-muted">DELETE</code> statements to your
                database. It cannot be undone from here.
              </>
            )
          }
        />
        <BulkEditModal
          open={bulkEditOpen}
          onCancel={() => {
            if (bulkEditMutation.isPending) return;
            setBulkEditOpen(false);
          }}
          onApply={applyBulkEdit}
          fields={schema ?? []}
          count={selectedIds.length}
          applying={
            bulkEditMutation.isPending || (bulkEditOpen && bulkProgress != null)
          }
          progress={
            bulkProgress && (bulkEditMutation.isPending || bulkEditOpen)
              ? `Updating ${bulkProgress.done} of ${bulkProgress.total}`
              : undefined
          }
          collection={collectionDto}
          demoMode={demoMode}
        />
        {!hideWriteChrome && (
        <ConfirmDeleteModal
          open={importOpen}
          onCancel={() => {
            if (importMutation.isPending) return;
            setImportOpen(false);
          }}
          onConfirm={() => {}}
          title={`Import ${bulkProgress?.total ?? 0} ${(bulkProgress?.total ?? 0) === 1 ? "record" : "records"}`}
          confirmLabel={`Import ${bulkProgress?.total ?? 0}`}
          applying={
            importMutation.isPending || (importOpen && bulkProgress != null)
          }
          progress={
            bulkProgress && (importMutation.isPending || importOpen)
              ? `Importing ${bulkProgress.done} of ${bulkProgress.total}`
              : undefined
          }
          body={
            <>
              This writes {bulkProgress?.total ?? 0} separate{" "}
              <code className="text-od-muted">POST</code> creates to your
              database.
            </>
          }
        />
        )}
      </>
    ),
    [
      datasourceId,
      collection,
      data,
      ruleBroken,
      isLoading,
      syncByRow,
      syncReady,
      syncSpreadsheetId,
      syncActions,
      updateMutation.isPending,
      deleteMutation.isPending,
      total,
      schema,
      idField,
      sortModel,
      handleSortModelChange,
      handleRowUpdate,
      onRowClick,
      onRowDoubleClick,
      onDrawerTarget,
      debugMode,
      paginationModel,
      onPaginationChange,
      refetch,
      expandedRelations,
      handleExpansionChange,
      columnVisibility,
      handleColumnVisibilityChange,
      columnOrder,
      handleColumnOrderChange,
      expandableFieldsMap,
      density,
      demoMode,
      readOnly,
      hideWriteChrome,
      hidePagination,
      hasActiveFilters,
      reconnect,
      search,
      handleSearchChange,
      onClearFilters,
      onAddFilter,
      rowToDelete,
      confirmDelete,
      selectedIds,
      bulkDeleteOpen,
      confirmBulkDelete,
      bulkDeleteMutation.isPending,
      bulkEditOpen,
      applyBulkEdit,
      bulkEditMutation.isPending,
      importMutation.isPending,
      importOpen,
      bulkProgress,
      collectionDto,
      handleExport,
      handleSelectMatching,
      selectMatchingPending,
    ]
  );

  return {
    Toolbar,
    DataTable,
    data,
    loading:
      isLoading ||
      updateMutation.isPending ||
      deleteMutation.isPending ||
      bulkDeleteMutation.isPending ||
      bulkEditMutation.isPending ||
      importMutation.isPending,
    fetching: isFetching,
    error,
    reconnect,
    fetchData: refetch,
    total,
    paginationModel,
    onPaginationChange,
  };
};

/** The edited cells only, for the optimistic row in the table cache. */
const payloadWithoutKeys = (
  row: any,
  original: any,
  schema?: Field[]
): Record<string, any> => buildPayload(schema ?? [], row, original);

const describeRow = (row: any, idField?: string): string => {
  if (!row) return "";
  const label =
    row.name ?? row.title ?? row.label ?? row[idField || "id"] ?? undefined;
  return label != null ? String(label) : "";
};
